import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Button } from "@heroui/react";
import SearchFilter from "../../../utils/SearchFilter";
import {
  JODI_REPORT_ENDPOINTS,
  createJodiReportDatasource,
  slNoColumn,
} from "../../../utils/jodiReportServerSide";

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 20;

const ShowAllBetsButton = (props) => {
  const { data } = props;
  const { onShowAllBets } = props.context;

  return (
    <button
      className="bg-indigo-500 text-white px-2 py-1 rounded text-xs"
      onClick={() => onShowAllBets(data.user_id)}
    >
      Show All Bets
    </button>
  );
};

const JodiReportUsersTable = ({ basePath = "/admin" }) => {
  const { matchId, digit } = useParams();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const searchRef = useRef("");
  const [matchInfo, setMatchInfo] = useState(null);
  const [gridReady, setGridReady] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");

  const handleShowAllBets = useCallback(
    (userId) => {
      navigate(`${basePath}/jodi-report-bets/${matchId}/${userId}/${digit}`);
    },
    [navigate, basePath, matchId, digit]
  );

  const columnDefs = useMemo(
    () => [
      slNoColumn,
      { headerName: "User ID", field: "user_id", colId: "user_id", flex: 1 },
      { headerName: "Username", field: "username", colId: "username", flex: 1 },
      {
        headerName: "Stake",
        field: "stake",
        colId: "stake",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "0.00",
      },
      {
        headerName: "Payout",
        field: "payout",
        colId: "payout",
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toFixed(2) : "0.00",
      },
      {
        headerName: "Bet Placed Time",
        field: "bet_time",
        colId: "bet_time",
        flex: 1,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleString() : "—",
      },
      {
        headerName: "Actions",
        field: "actions",
        cellRenderer: ShowAllBetsButton,
        suppressMenu: true,
        sortable: false,
        filter: false,
        maxWidth: 150,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
    }),
    []
  );

  const datasource = useMemo(
    () =>
      createJodiReportDatasource({
        endpoint: JODI_REPORT_ENDPOINTS.users,
        getBasePayload: () => ({
          matchId: parseInt(matchId, 10),
          digit,
        }),
        getSearch: () => searchRef.current,
        onMeta: (data) => {
          if (data?.match) setMatchInfo(data.match);
        },
      }),
    [matchId, digit]
  );

  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params.api;
      params.api.setGridOption("datasource", datasource);
      setGridReady(true);
    },
    [datasource]
  );

  useEffect(() => {
    searchRef.current = quickFilterText;
    if (!gridReady || !gridRef.current) return;

    const timer = setTimeout(() => {
      gridRef.current.refreshInfiniteCache();
    }, 300);

    return () => clearTimeout(timer);
  }, [quickFilterText, gridReady]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Jodi Report — Users (Digit: {digit})
          </h2>
          {matchInfo ? (
            <p className="text-sm text-gray-600 mt-1">
              {matchInfo.market_name} — {matchInfo.name}
            </p>
          ) : (
            <p className="text-sm text-gray-400 mt-1">Loading match details...</p>
          )}
        </div>
        <Button
          color="default"
          variant="flat"
          onPress={() => navigate(`${basePath}/jodi-report/${matchId}`)}
        >
          Back to Jodi Report
        </Button>
      </div>

      <SearchFilter
        value={quickFilterText}
        onChange={(e) => setQuickFilterText(e.target.value)}
      />

      <div className="ag-theme-alpine" style={{ height: 700, width: "100%" }}>
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={5}
          pagination={true}
          paginationPageSize={PAGE_SIZE}
          onGridReady={onGridReady}
          onSortChanged={(params) => params.api.refreshInfiniteCache()}
          context={{ onShowAllBets: handleShowAllBets }}
          enableCellTextSelection={true}
          overlayNoRowsTemplate="<span style='padding: 10px; display: block;'>No users found for this number</span>"
        />
      </div>
    </div>
  );
};

export default JodiReportUsersTable;
